/**
 * Work Queue resource service (GS).
 *
 * Infrastructure (client + envelope helpers) landed in CT-5090. CT-5091 adds the
 * inbox list. There is no unified Work Queue events endpoint yet — it is owned by
 * CT-4867. Until it ships, {@link aggregateWorkQueueEvents} assembles the inbox by
 * fanning out over the six governance "My Work" lists and normalizing each source
 * into the Work Queue event shape. {@link listWorkQueueEvents} is the forward-looking
 * seam that will replace the fan-out with a single call once CT-4867 exists.
 *
 * Every helper here:
 *   1. POSTs to a GS path via `gsApi` (services/apiClient.js).
 *   2. Unwraps the envelope with `validateApiResponse` (shared/utils/apiResponse.js) —
 *      GS returns HTTP 200 even on failure, so the transport status can't be trusted.
 *   3. Returns plain data (`{ items, meta }` or an array) — never the raw Axios response.
 *
 * NOTE (Bearer, from CT-5090): `apiClient` sends `Authorization: Bearer <token>`.
 * The beta MyWork doc says GS may expect the token WITHOUT the prefix. This is the
 * first story to make real GS calls — if they return 401/403, drop the prefix in
 * services/apiClient.js with live evidence.
 */

import { gsApi } from './apiClient.js';
import { validateApiResponse } from '../shared/utils/apiResponse.js';
import {
  claimHasPendingSuggestion,
  mapChangeRequestToEvent,
  mapClaimToEvent,
  mapTaskToEvent,
} from '../domains/work-queue/utils/mapSourceToEvent.js';
import { mapApiEventToUi } from '../domains/work-queue/utils/mapApiEventToUi.js';
import { getDevSeedTasks } from '../domains/work-queue/constants/devSeedTasks.js';
import { getViewerUuid } from '../domains/work-queue/utils/taskOwner.js';
import { mapHitlTaskToEvent } from '../domains/work-queue/utils/mapHitlToEvent.js';
import { resolveUserNames } from './userDirectory.js';
import { listHitlTasks } from './hitlActions.js';
import { listPromotions } from './promotionActions.js';
import { mapPromotionToEvent } from '../domains/work-queue/utils/mapPromotionToEvent.js';

/** Page size for the per-source fan-out lists (interim; one page per source). */
const FANOUT_PAGE_SIZE = 50;
/** Page size for the KB/sandbox source discovery lists. */
const SOURCE_PAGE_SIZE = 50;
/** Only pending change requests belong in the inbox. */
const PENDING_CHANGE_STATUS = 'PENDING_APPROVAL';
/**
 * Cap parallel per-KB / per-sandbox lists. A tenant with ~35 KBs + ~37 sandboxes
 * would otherwise open ~110 POSTs at once (plus a CORS OPTIONS each — GS sends
 * `Access-Control-Max-Age: 0`). The browser then cancels the overflow.
 */
const FANOUT_CONCURRENCY = 4;

/** In-flight inbox assemblies, keyed by scope, so StrictMode/remount joins one run. */
const inflightAggregates = new Map();

/** Test-only: drop in-flight dedupe between cases. */
export function resetWorkQueueAggregateInflight() {
  inflightAggregates.clear();
}

/** POST + envelope-unwrap. Returns the normalized `{ body, meta, ... }`. */
async function post(path, payload, errorMessage) {
  const res = await gsApi.post(path, payload);
  return validateApiResponse(res, errorMessage);
}

/**
 * Run `taskFns` with at most `concurrency` in flight. Each fn is created lazily
 * so we do not stampede GS the way `Promise.all(items.map(fetch))` does.
 */
async function allSettledPool(taskFns, concurrency = FANOUT_CONCURRENCY) {
  if (taskFns.length === 0) return [];
  const results = new Array(taskFns.length);
  let next = 0;
  async function worker() {
    for (;;) {
      const index = next;
      next += 1;
      if (index >= taskFns.length) return;
      try {
        results[index] = { status: 'fulfilled', value: await taskFns[index]() };
      } catch (reason) {
        results[index] = { status: 'rejected', reason };
      }
    }
  }
  const workers = Math.min(concurrency, taskFns.length);
  await Promise.all(Array.from({ length: workers }, () => worker()));
  return results;
}

const toArray = (body) => (Array.isArray(body) ? body : []);

// NOTE (request keys): the general platform convention is snake_case bodies, but
// the GS `my-work` endpoint family expects camelCase params (`pageSize`,
// `orderBy`, `orderDirection`, `knowledgebaseUuid`, `sandboxUuid`). This is
// verified — it matches the proven beta client (web-app-governance-studio
// myWork.js) and a live 2026-08-31 curl (HTTP 200). Do NOT "correct" these to
// snake_case; GS rejects that. (`sandbox/list` additionally rejects any
// `orderBy`/`orderDirection`, so those are omitted from listSandboxes.)

// ── Source discovery ────────────────────────────────────────────────────────

/** POST /v1/my-work/totals — server-side inbox counters (CT-5091). Best-effort. */
export async function listMyWorkTotals() {
  try {
    const { body } = await post('/v1/my-work/totals', {}, 'Failed to load work queue totals');
    return body ?? null;
  } catch {
    return null;
  }
}

/** KBs the current user can access. `meta` is snake_case on this endpoint. */
export async function listKnowledgeBases({ page = 1, pageSize = SOURCE_PAGE_SIZE } = {}) {
  const { body, meta } = await post(
    '/v1/my-work/knowledgebase/list',
    { page, pageSize, orderBy: 'updated_at', orderDirection: 'DESC' },
    'Failed to load knowledge bases',
  );
  return { items: toArray(body), meta };
}

/**
 * Sandboxes the current user can access. This endpoint rejects string
 * `orderBy`/`orderDirection` with a 400 (MWSB001), so those are intentionally
 * omitted from the payload.
 */
export async function listSandboxes({ page = 1, pageSize = SOURCE_PAGE_SIZE } = {}) {
  const { body, meta } = await post(
    '/v1/my-work/sandbox/list',
    { page, pageSize },
    'Failed to load sandboxes',
  );
  return { items: toArray(body), meta };
}

// ── Per-source lists (fanned out) ───────────────────────────────────────────

/** Pending change requests for one KB. */
export async function listChangeRequests({ knowledgebaseUuid, page = 1, pageSize = FANOUT_PAGE_SIZE } = {}) {
  const { body } = await post(
    '/v1/my-work/change-request/list',
    {
      knowledgebaseUuid,
      page,
      pageSize,
      orderBy: 'updated_at',
      orderDirection: 'DESC',
      filters: { changeStatus: PENDING_CHANGE_STATUS },
    },
    'Failed to load change requests',
  );
  return toArray(body);
}

/** Claim suggestions for one sandbox. */
export async function listClaimSuggestions({ sandboxUuid, page = 1, pageSize = FANOUT_PAGE_SIZE } = {}) {
  const { body } = await post(
    '/v1/my-work/claim-suggestion/list',
    { sandboxUuid, page, pageSize, orderBy: 'updated_at', orderDirection: 'DESC' },
    'Failed to load claim suggestions',
  );
  return toArray(body);
}

/**
 * Tasks assigned to the CALLER. There is deliberately no `assigneeUuid` parameter:
 * the endpoint derives the assignee from the auth context and refuses to honor an
 * override — its own request model says so ("The assignee is always derived from
 * the auth context in the handler; callers cannot override it"), and it was
 * verified live: sending another user's uuid returns 200 with the caller's own
 * rows. Passing one would therefore present your tasks as a teammate's, silently.
 * A manager-scoped read needs a backend change (CT-4867), not a new parameter.
 */
export async function listMyWorkTasks({ status = 'open', page = 1, pageSize = FANOUT_PAGE_SIZE } = {}) {
  const payload = { page, pageSize };
  if (status) payload.status = status;
  const { body } = await post('/v1/task/my-work/list', payload, 'Failed to load tasks');
  return toArray(body);
}

// ── Aggregation ─────────────────────────────────────────────────────────────

/**
 * Collects fulfilled fan-out rows, mapping each with `adapt(row, context, index)`.
 * An adapter may return a falsy value to skip a row (e.g. a claim with no pending
 * suggestion); the running index is used for synthetic-id fallbacks.
 */
function collect(settledResults, adapt) {
  const events = [];
  settledResults.forEach((result) => {
    if (result.status !== 'fulfilled') return;
    const { context, rows } = result.value;
    rows.forEach((row) => {
      const event = adapt(row, context, events.length);
      if (event) events.push(event);
    });
  });
  return events;
}

/**
 * Interim inbox assembler (CT-5091). Loads every KB and sandbox the user can
 * reach, fans out to their pending change requests / proposals / claim
 * suggestions, and pulls the caller's cross-plane tasks — then normalizes all of
 * it into UI events via the {@link mapApiEventToUi} seam.
 *
 * Resilient by design: a single KB/sandbox failing (via `Promise.allSettled`)
 * drops only its slice, never the whole list. If BOTH source-discovery calls
 * fail, the error propagates so the provider can surface an error state.
 *
 * @param {Object} [params]
 * @param {'my_work'|'my_team'} [params.scope='my_work'] - tags each event's scope.
 *
 * Note on `my_team`: this runs the SAME fan-out, only tagged, minus HITL and
 * promotions (both caller-scoped). It does not fetch anyone else's work — no
 * endpoint allows that today, so `scopeEventsForPersona` narrows what is shown
 * client-side. A real team scope is a CT-4867 ask.
 * @returns {Promise<{ events: Object[], total: number }>}
 */
async function runAggregateWorkQueueEvents({ scope = 'my_work' } = {}) {
  const [kbResult, sandboxResult, totalsResult] = await Promise.allSettled([
    listKnowledgeBases(),
    listSandboxes(),
    listMyWorkTotals(),
  ]);

  if (kbResult.status === 'rejected' && sandboxResult.status === 'rejected') {
    throw kbResult.reason ?? new Error('Failed to load Work Queue sources');
  }

  const kbs = kbResult.status === 'fulfilled' ? kbResult.value.items : [];
  const sandboxes = sandboxResult.status === 'fulfilled' ? sandboxResult.value.items : [];

  // HITL tasks live on Platform (not GS) and are always scoped to the auth user,
  // so they only belong to My Work.
  const hitlPromise =
    scope === 'my_work'
      ? listHitlTasks({ status: 'pending' }).catch(() => ({ items: [] }))
      : Promise.resolve({ items: [] });
  // Draft promotion packages are workspace-scoped (not per-KB/sandbox) — one list call.
  // TEMPORARY: POST /v1/workspace/promotion/list until My Work promotions list ships (CT-4867).
  // Only 'draft' is requested: filtering by 'queued' is currently broken on the backend
  // (POST /v1/workspace/promotion/list with filters.statuses: ['queued'] returns zero
  // rows even for promotions that genuinely have status 'queued' — verified live via
  // curl 2026-09-23, reported separately). Revisit once that's fixed.
  const promotionsPromise =
    scope === 'my_work'
      ? listPromotions({ pageSize: FANOUT_PAGE_SIZE, filters: { statuses: ['draft'] } }).catch(() => ({
          items: [],
        }))
      : Promise.resolve({ items: [] });

  // Proposals (POST /v1/my-work/proposal/list) are intentionally NOT fanned into
  // the inbox (CT-5366): that endpoint returns one row per individual claim with
  // no grouping by promotion package, so claims promoted together in one package
  // showed up as separate "Proposal" cards instead of one Gov Promotion card.
  const [crResults, claimResults, tasks, hitlResult, promotionsResult] = await Promise.all([
    allSettledPool(
      kbs.map((kb) => () =>
        listChangeRequests({ knowledgebaseUuid: kb.knowledgebaseUuid }).then((rows) => ({ context: kb, rows })),
      ),
    ),
    allSettledPool(
      sandboxes.map((sb) => () =>
        listClaimSuggestions({ sandboxUuid: sb.sandboxUuid }).then((rows) => ({ context: sb, rows })),
      ),
    ),
    listMyWorkTasks({ status: 'open' }).catch(() => []),
    hitlPromise,
    promotionsPromise,
  ]);
  const hitlTasks = hitlResult.items ?? [];
  const promotions = promotionsResult.items ?? [];

  // Resolve assigneeUuid → fullName for task owners (CT-5094). Per-uuid via
  // GET /v1/tenant/user/{uuid}, deduped + cached — only the distinct owners in the
  // inbox are fetched (no 100-row picker cap). Best-effort: an unresolved uuid is
  // omitted and its owner chip falls back to the raw uuid.
  const ownerUuids = [...tasks, ...getDevSeedTasks()].map((task) => task.assigneeUuid);
  const nameById = await resolveUserNames(ownerUuids).catch(() => ({}));
  const ownerContext = { nameById, viewerUuid: getViewerUuid() };

  const rawEvents = [
    ...collect(crResults, (row, kb, i) => mapChangeRequestToEvent(row, kb, scope, i)),
    ...collect(claimResults, (row, sb, i) =>
      (claimHasPendingSuggestion(row) ? mapClaimToEvent(row, sb, scope, i) : null),
    ),
    // DEV-ONLY: contract-shaped seed tasks (CT-5093) when the flag is set. Returns
    // [] in prod and in a normal dev session — see constants/devSeedTasks.js.
    ...[...tasks, ...getDevSeedTasks()].map((task, i) => mapTaskToEvent(task, scope, i, ownerContext)),
    ...hitlTasks
      .map((task, i) => mapHitlTaskToEvent(task, scope, i))
      .filter(Boolean),
    ...promotions.map((row) => mapPromotionToEvent(row, scope)).filter(Boolean),
  ];

  const events = rawEvents
    .filter((raw) => raw.id != null)
    .map((raw) => mapApiEventToUi(raw));

  const warnings = [];
  if (kbResult.status === 'rejected') warnings.push('knowledge_bases');
  if (sandboxResult.status === 'rejected') warnings.push('sandboxes');
  const countRejected = (settled) => settled.filter((result) => result.status === 'rejected').length;
  if (countRejected(crResults) > 0) warnings.push('change_requests');
  if (countRejected(claimResults) > 0) warnings.push('claim_suggestions');

  const serverTotals = totalsResult.status === 'fulfilled' ? totalsResult.value : null;

  return { events, total: events.length, userNameById: nameById, warnings, serverTotals };
}

export function aggregateWorkQueueEvents(params = {}) {
  const scope = params.scope ?? 'my_work';
  const key = scope;
  const existing = inflightAggregates.get(key);
  if (existing) return existing;
  const pending = runAggregateWorkQueueEvents({ ...params, scope }).finally(() => {
    if (inflightAggregates.get(key) === pending) inflightAggregates.delete(key);
  });
  inflightAggregates.set(key, pending);
  return pending;
}

/**
 * Forward-looking unified list call (CT-4867). Not wired yet — the endpoint does
 * not exist. Once it ships, the provider swaps `aggregateWorkQueueEvents` for
 * this single server-side query (scope/filters/sort/pagination handled by GS).
 *
 * @param {Object} payload - `{ scope, filters, sort, page, pageSize }`.
 * @returns {Promise<{ body: Object[], meta: Object|null }>}
 */
export async function listWorkQueueEvents(payload = {}) {
  const { body, meta } = await post('/v1/work-queue/events/list', payload, 'Failed to load work queue');
  return { body: toArray(body), meta };
}
